import React, { useEffect, useRef, useState } from "react";
import { Loader2, UserPlus, RefreshCw, Link2, Search, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { patientView, searchPatients, setNotePatient, rematchNote, getNote } from "@/lib/noteClaim";

const BADGE = {
  matched: "bg-[#e7f6ee] text-[#1f9d57]",
  tentative: "bg-[#fbf1de] text-[#b06f00]",
  nomatch: "bg-[#fbe9e7] text-[#c22219]",
};

// Patient as the document reads it — name + MRN (matched) or name + DOB with a badge (no match).
export function PatientBadge({ note, testid }) {
  const v = patientView(note);
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[12px] font-semibold text-[#1d1d1f] truncate">{v.name}</div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className={`text-[9px] font-bold px-1.5 py-[1px] rounded-full ${BADGE[v.badge]}`}>{v.label}</span>
        {v.detail ? <span className="text-[10.5px] text-[#6e6e73] truncate">{v.detail}</span> : null}
      </div>
    </div>
  );
}

// Fix a no-match: search + link an existing (roster or live EHI) patient, add a new roster
// patient (dedups to an existing one), or re-match from the document. Calls onResolved(note).
export function NoMatchFixer({ note, clientId, onResolved, compact = true }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("link"); // link | add
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", date_of_birth: "", gender: "" });
  const tRef = useRef(null);

  useEffect(() => {
    if (!open || tab !== "link") return;
    if (!q.trim()) { setResults(null); return; }
    clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      setSearching(true);
      try { setResults(await searchPatients(clientId, q)); } catch { setResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [q, open, tab, clientId]);

  const afterWrite = async (res) => {
    const m = res?.match;
    const fresh = await getNote(note.id).catch(() => null);
    if (m && m.status === "matched") toast.success(`Linked to ${fresh?.patient_name || "patient"}${m.mrn ? ` · MRN ${m.mrn}` : ""}`);
    else if (m) toast.message("Saved — still no roster match", { description: m.detail || "" });
    onResolved?.(fresh || note);
    setOpen(false); setQ(""); setResults(null);
  };

  const linkTo = async (p) => {
    setBusy(true);
    try {
      const res = await setNotePatient(note.id, {
        first_name: p.first_name || "", last_name: p.last_name || "",
        date_of_birth: p.dob || p.date_of_birth || "", gender: p.gender || "", mrn: p.mrn || "",
      });
      await afterWrite(res);
    } catch (e) { toast.error(e?.message || "Could not link that patient."); } finally { setBusy(false); }
  };

  const addNew = async () => {
    if (!form.last_name.trim() || !form.date_of_birth) { toast.error("Last name and date of birth are needed."); return; }
    setBusy(true);
    try { await afterWrite(await setNotePatient(note.id, form)); }
    catch (e) { toast.error(e?.message || "Could not add that patient."); } finally { setBusy(false); }
  };

  const rematch = async () => {
    setBusy(true);
    try {
      const n = await rematchNote(note.id);
      const fresh = n && n.id ? n : await getNote(note.id).catch(() => null);
      const v = patientView(fresh || note);
      v.badge === "matched" ? toast.success(`Re-matched to ${v.name}`) : toast.message("Re-matched — still no match from the document");
      onResolved?.(fresh || note); setOpen(false);
    } catch (e) { toast.error(e?.message || "Could not re-match."); } finally { setBusy(false); }
  };

  const Inp = (k, ph, type = "text") => (
    <input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} type={type}
      className="text-[11.5px] border border-black/[.12] rounded-[8px] px-2.5 py-1.5 bg-white min-w-0" data-testid={`nmf-add-${k}`} />
  );

  return (
    <div className="mt-2" data-testid={`nmf-${note.id}`}>
      {!open ? (
        <div className="flex items-center gap-2">
          <button onClick={() => { setOpen(true); setTab("link"); }} className="text-[10.5px] font-semibold text-[#5856D6] inline-flex items-center gap-1" data-testid={`nmf-open-${note.id}`}><Link2 size={11} /> Link patient</button>
          <span className="text-black/15">·</span>
          <button onClick={rematch} disabled={busy} className="text-[10.5px] font-semibold text-[#6e6e73] inline-flex items-center gap-1 disabled:opacity-50" data-testid={`nmf-rematch-${note.id}`}>{busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Re-match</button>
        </div>
      ) : (
        <div className="rounded-[10px] border border-black/[.1] bg-white p-2.5">
          <div className="flex items-center gap-1 mb-2">
            <button onClick={() => setTab("link")} className={`text-[10.5px] font-bold px-2 py-1 rounded-[7px] ${tab === "link" ? "bg-black/[.06] text-[#1d1d1f]" : "text-[#6e6e73]"}`} data-testid={`nmf-tab-link-${note.id}`}>Link existing</button>
            <button onClick={() => setTab("add")} className={`text-[10.5px] font-bold px-2 py-1 rounded-[7px] ${tab === "add" ? "bg-black/[.06] text-[#1d1d1f]" : "text-[#6e6e73]"}`} data-testid={`nmf-tab-add-${note.id}`}>Add new</button>
            <span className="flex-1" />
            <button onClick={() => setOpen(false)} className="text-[10.5px] font-semibold text-[#6e6e73]">Cancel</button>
          </div>
          {tab === "link" ? (
            <>
              <div className="flex items-center gap-1.5 border border-black/[.12] rounded-[8px] px-2 py-1.5 bg-white">
                <Search size={12} className="text-[#aeaeb2]" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roster or EHI chart by name or MRN"
                  className="text-[11.5px] outline-none bg-transparent w-full" data-testid={`nmf-search-${note.id}`} />
                {searching ? <Loader2 size={12} className="animate-spin text-[#aeaeb2]" /> : null}
              </div>
              <div className="max-h-[160px] overflow-y-auto mt-1.5">
                {results == null ? <div className="text-[10.5px] text-[#9b9b97] px-1 py-1.5">Start typing to find a patient.</div>
                  : !results.length ? <div className="text-[10.5px] text-[#9b9b97] px-1 py-1.5">No patient found — try “Add new”.</div>
                  : results.map((p) => (
                    <button key={p.mrn || p.chart_uuid || p.full_name} onClick={() => linkTo(p)} disabled={busy}
                      className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-[8px] hover:bg-black/[.04] disabled:opacity-50" data-testid={`nmf-result-${note.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-[11.5px] font-semibold text-[#1d1d1f] truncate">{p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim()}</div>
                        <div className="text-[10px] text-[#6e6e73] truncate">{p.dob ? `DOB ${p.dob}` : ""}{p.mrn ? ` · MRN ${p.mrn}` : ""}</div>
                      </div>
                      <span className="text-[9px] font-bold px-1.5 py-[1px] rounded-full bg-black/[.05] text-[#6e6e73]">{p.chart_uuid ? "EHI chart" : "Roster"}</span>
                      <Link2 size={12} className="text-[#5856D6] flex-none" />
                    </button>
                  ))}
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {Inp("first_name", "First name")}
              {Inp("last_name", "Last name")}
              {Inp("date_of_birth", "DOB (YYYY-MM-DD)")}
              {Inp("gender", "Gender")}
              <button onClick={addNew} disabled={busy} className="col-span-2 mt-0.5 text-[11.5px] font-bold text-white rounded-[8px] px-3 py-1.5 bg-[#1d1d1f] inline-flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid={`nmf-add-save-${note.id}`}>
                {busy ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />} Add &amp; link
              </button>
              <div className="col-span-2 text-[10px] text-[#9b9b97]">If this patient already exists, it links to that record instead of making a duplicate.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Matched → just the badge; otherwise badge + the fixer controls.
export function PatientMatchCell({ note, clientId, onResolved }) {
  const v = patientView(note);
  return (
    <div className="min-w-0">
      <PatientBadge note={note} testid={`pm-badge-${note.id}`} />
      {v.badge !== "matched" ? <NoMatchFixer note={note} clientId={clientId} onResolved={onResolved} /> : (
        <div className="flex items-center gap-1 mt-1 text-[10px] text-[#1f9d57]"><CheckCircle2 size={10} /> linked</div>
      )}
    </div>
  );
}
